"use client";

import { motion } from "framer-motion";
import {
  Code2,
  BarChart3,
  ShieldCheck,
  CandlestickChart,
  type LucideIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";

const featureDefs: {
  icon: LucideIcon;
  titleKey:
    | "featureNlTitle"
    | "featureAnalyticsTitle"
    | "featureValidationTitle"
    | "featureTerminalTitle";
  descKey:
    | "featureNlDesc"
    | "featureAnalyticsDesc"
    | "featureValidationDesc"
    | "featureTerminalDesc";
  featured?: boolean;
}[] = [
  {
    icon: Code2,
    titleKey: "featureNlTitle",
    descKey: "featureNlDesc",
  },
  {
    icon: BarChart3,
    titleKey: "featureAnalyticsTitle",
    descKey: "featureAnalyticsDesc",
  },
  {
    icon: ShieldCheck,
    titleKey: "featureValidationTitle",
    descKey: "featureValidationDesc",
  },
  {
    icon: CandlestickChart,
    titleKey: "featureTerminalTitle",
    descKey: "featureTerminalDesc",
    featured: true,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45 },
  },
};

/**
 * Four claims: code, engine, reject, Terminal live.
 */
export function FeatureGrid() {
  const t = useTranslations("Home");

  return (
    <section className="bg-slate-900/50 px-4 py-24 sm:px-6">
      <div className="mx-auto w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-teal-light">
            {t("featuresEyebrow")}
          </p>
          <h2 className="mx-auto mb-4 max-w-[20.5rem] text-[clamp(1.5rem,6vw,3rem)] font-bold tracking-tight text-pretty text-slate-100 sm:max-w-none">
            {t("featuresTitle")}
          </h2>
          <p className="mx-auto w-full max-w-[20.5rem] text-base leading-relaxed text-slate-400 sm:max-w-2xl sm:text-lg">
            {t("featuresIntro")}
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid min-w-0 gap-6 md:grid-cols-2"
        >
          {featureDefs.map((feature) => (
            <motion.article
              key={feature.titleKey}
              variants={itemVariants}
              className={`group min-w-0 rounded-2xl border p-8 transition-all duration-300 ${
                feature.featured
                  ? "border-maroon/40 bg-gradient-to-br from-maroon/15 via-slate-800/40 to-slate-900/60 hover:border-maroon/60"
                  : "border-slate-700/50 bg-slate-800/30 hover:border-teal-primary/40 hover:bg-slate-800/50"
              }`}
            >
              <div
                className={`mb-6 flex h-12 w-12 items-center justify-center rounded-lg transition-colors ${
                  feature.featured
                    ? "bg-maroon/25 group-hover:bg-maroon/35"
                    : "bg-teal-primary/20 group-hover:bg-teal-primary/30"
                }`}
              >
                <feature.icon
                  className={`h-6 w-6 ${feature.featured ? "text-maroon" : "text-teal-light"}`}
                />
              </div>
              <h3 className="mb-3 text-xl font-semibold text-slate-100">
                {t(feature.titleKey)}
              </h3>
              <p className="w-full min-w-0 break-words leading-relaxed text-pretty text-slate-400">
                {t(feature.descKey)}
              </p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
